// lib/services/shader/shader_service.ts

"use client";

import { rb_error } from "../compute/rb_log";
import {assetCache,isCacheableShader} from "../resource/asset-cache";
import {loadingState} from "../resource/loading-state";

type ShaderDef = {
  name: string;
  headers: string[];
};

function SHADER(name: string, ...headers: string[]): ShaderDef {
  return { name, headers };
}

export default class ShaderService {
  private device: GPUDevice | null = null;
  private adapter: GPUAdapter | null = null;

  private header_sources: Map<string, string> = new Map();
  private compiled_compute: Map<string, GPUShaderModule> = new Map();
  private compiled_render: Map<string, GPUShaderModule> = new Map();

  private initialized = false;
  private initializing:Promise<void>|null=null;
  private visionInitialized=false;
  private visionInitializing:Promise<void>|null=null;

  private header_names: string[] = [
    "animation_types",
    // Shared inverted-hull outline extrusion/uniform header (see
    // outline_common.wgsl) - consumed by outline_static/outline_skinned.
    "outline_common",
    // Shared color management and studio lighting for textured 3D robots.
    "robot_studio_common",
    // Sticker-scanner shader headers (shared WGSL included into the compute
    // shaders below).
    "particle_types",
    "colortypes",
    "black_and_white_helpers",
    "circle_helpers",
    "circle_helpers_nv12",
    "circle_helpers_i420",
    "nv12_helpers",
    "i420_helpers",
    "trigtables360",
    "local_bias",
    "lookhelpers",
    "look_2x2",
    "look_3x3",
    "draw_helpers",
  ];

  private compute_defs: ShaderDef[] = [
    SHADER("animation_controller", "animation_types"),
    // Sticker-scanner compute pipeline (black-white grid → mask → blob → 3x3
    // decode). Ported from Sweet Tooth; drives StickerCompute.
    SHADER("black_white_grid_nv12", "colortypes"),
    SHADER("black_white_grid_i420", "colortypes"),
    SHADER("black_white_grid_reduce", "colortypes"),
    SHADER("black_white_mask_a_nv12", "colortypes", "black_and_white_helpers"),
    SHADER("black_white_mask_a_i420", "colortypes", "black_and_white_helpers"),
    SHADER("black_white_mask_b_nv12", "colortypes", "black_and_white_helpers"),
    SHADER("black_white_mask_b_i420", "colortypes", "black_and_white_helpers"),
    SHADER("clear_blobs_basic", "colortypes"),
    SHADER("clear_blobs", "colortypes"),
    SHADER("clear_sticker_result", "colortypes"),
    SHADER("clearblobtex", "colortypes"),
    SHADER("composebloblista", "colortypes"),
    SHADER("composebloblistb", "colortypes"),
    SHADER("fillblobsmapx_bw", "colortypes"),
    SHADER("fillblobsmapy_bw", "colortypes"),
    SHADER("filterblobscontrast_nv12", "colortypes"),
    SHADER("filterblobscontrast_i420", "colortypes"),
    SHADER("filterblobsneighbors", "colortypes"),
    SHADER("findnearestneighbor3x3", "colortypes"),
    SHADER("get_dirangle_and_colors_nv12", "colortypes", "circle_helpers", "circle_helpers_nv12", "nv12_helpers", "trigtables360", "local_bias"),
    SHADER("get_dirangle_and_colors_i420", "colortypes", "circle_helpers", "circle_helpers_i420", "i420_helpers", "trigtables360", "local_bias"),
    SHADER("lookforstickersall", "colortypes", "lookhelpers", "look_2x2", "look_3x3"),
    SHADER("pack_sticker_codes", "colortypes"),
    SHADER("debug_blob_basic", "colortypes", "draw_helpers"),
    SHADER("debug_blob", "colortypes", "draw_helpers"),
    SHADER("debug_blob_dirangle", "colortypes", "draw_helpers"),
    SHADER("pack_blobs", "colortypes"),
     SHADER("particle_compute", "particle_types"),
  ];

  private render_defs: ShaderDef[] = [
    SHADER("grid_shader"),
    SHADER("canvas_color"),
    SHADER("unlit_texture"),
    SHADER("unlit_texture_skinned"),
    SHADER("papercraft_skinned"),
    SHADER("blob_shadow"),
    SHADER("boneyard_blob_shadow"),
    SHADER("collision_shader"),
    SHADER("graph_shader"),
    SHADER("papercraft_static"),
    SHADER("robot_studio_static", "robot_studio_common"),
    SHADER("robot_studio_skinned", "robot_studio_common"),
    // Isolated opt-in Robot02 finish comparison; shared Studio shaders unchanged.
    SHADER("robot02_review_skinned", "robot_studio_common"),
    SHADER("unlit_texture_alpha"),
    SHADER("boneyard_foreground_cutout"),
    SHADER("unlit_texture_alpha_instanced"),
    SHADER("static_mesh_instanced"),
    SHADER("boneyard_power_fade"),
    SHADER("boneyard_pickup_sunlit"),
    SHADER("boneyard_pickup_shadow"),
    SHADER("boneyard_crane_bolt_lights"),
    SHADER("boneyard_delivery_marker"),
    SHADER("boneyard_crane_occluder"),
    SHADER("boneyard_ground_lit_instanced"),
    SHADER("unlit_no_discard"),
    SHADER("storage_color"),
    // Camera-feed display (YUV→RGB) for the in-scene sticker scanner.
    SHADER("video_yuv_rgb_NV12"),
    SHADER("video_yuv_rgb_I420"),
    // Scanner visual feedback / debug overlays (Sweet Tooth Video parity).
    SHADER("scrim"),
    SHADER("debug_texture"),
    SHADER("debug_blob_overlay_nv12"),
    SHADER("debug_blob_overlay_i420"),
    SHADER("scan_feedback"),
    // Opt-in inverted-hull mesh outline pipelines (StaticOutlineRenderPipeline
    // / SkinnedOutlineRenderPipeline) - see outline_state.ts doc comment.
    SHADER("outline_static", "outline_common"),
    SHADER("outline_skinned", "outline_common"),
     SHADER("particle_render", "particle_types"),
  ];

  constructor() {}

  async init() {
    if (this.initialized) return;
    if(this.initializing)return this.initializing;
    this.initializing=this.initialize().catch(error=>{this.initializing=null;throw error;});
    return this.initializing;
  }

  private async initialize():Promise<void>{
    await assetCache.manifest();
    loadingState.phase("graphics");
    this.adapter = await navigator.gpu.requestAdapter();
    if (this.adapter) {
      this.device = await this.adapter.requestDevice();
      // Logging only: report device loss centrally. Does not replace any
      // existing handler (none is registered elsewhere) or alter recovery.
      this.device.lost.then((info) => {
        rb_error("device.lost", {
          reason: info.reason,
          message: info.message,
        });
      });
    }

    if(!this.device)throw new Error("A WebGPU graphics device could not be started.");
    await this.compileGroup(false);
    this.initialized = true;
  }

  async prepareVision():Promise<void>{
    await this.init();
    if(this.visionInitialized)return;
    if(!this.visionInitializing)this.visionInitializing=this.compileGroup(true)
      .then(()=>{this.visionInitialized=true;})
      .catch(error=>{this.visionInitializing=null;throw error;});
    return this.visionInitializing;
  }

  private async compileGroup(vision:boolean):Promise<void>{
    if(!this.device)throw new Error("Graphics device unavailable.");
    const belongs=(name:string)=>isCacheableShader(`${name}.wgsl`)!==vision;
    const compute=this.compute_defs.filter(def=>belongs(def.name));
    const render=this.render_defs.filter(def=>belongs(def.name));
    const headers=this.header_names.filter(belongs);
    const names=[...headers,...compute.map(d=>d.name),...render.map(d=>d.name)].map(n=>`${n}.wgsl`);
    const response=vision?await fetch("/api/shader-bundle",{
      method:"POST",cache:"no-store",headers:{"Content-Type":"application/json"},body:JSON.stringify({names})
    }):await assetCache.fetch("bundle","");
    if(!response.ok)throw new Error(`Could not prepare ${vision?"scanner":"game"} graphics.`);
    const {sources}=await response.json() as {sources:Record<string,string>};
    const localHeaders=new Map(this.header_sources);
    for(const name of headers){
      const source=sources[`${name}.wgsl`];
      if(typeof source!=="string")throw new Error(`Missing shader header: ${name}`);
      localHeaders.set(name,source);
      if(!vision)this.header_sources.set(name,source);
    }
    const jobs=[...compute.map(def=>({def,map:this.compiled_compute})),...render.map(def=>({def,map:this.compiled_render}))];
    let done=0;if(!vision)loadingState.shaders(0,jobs.length);
    try{
      await Promise.all(jobs.map(async({def,map})=>{
        const source=sources[`${def.name}.wgsl`];
        if(typeof source!=="string")throw new Error(`Missing shader: ${def.name}`);
        const module=this.device!.createShaderModule({label:def.name,code:this.assemble_source(def,source,localHeaders)});
        const info=await module.getCompilationInfo();
        const errors=info.messages.filter(message=>message.type==="error");
        if(errors.length)throw new Error(`Shader ${def.name} could not compile: ${errors[0].message}`);
        map.set(def.name,module);done++;
        if(!vision)loadingState.shaders(done,jobs.length);
      }));
    }finally{
      // Application references only. This is not a promise of secure memory
      // erasure or control over the browser/driver's own compiled-code caches.
      localHeaders.clear();
      for(const name of Object.keys(sources))delete sources[name];
    }
  }

  private assemble_source(def: ShaderDef, shader_source: string,headers=this.header_sources): string {
    if (def.headers.length === 0) {
      return shader_source;
    }

    const header_chunks: string[] = [];

    for (const header_name of def.headers) {
      const header_source = headers.get(header_name);
      if (!header_source) {
        throw new Error(
          `Shader "${def.name}" requires header "${header_name}" but the header was not loaded. ` +
            `Add "${header_name}" to the header_names list in ShaderService.`
        );
      }
      header_chunks.push(header_source);
    }

    return header_chunks.join("\n\n") + "\n\n" + shader_source;
  }

  async fetchShader(name: string): Promise<string> {
    if(isCacheableShader(name))return (await assetCache.fetch("shader",name)).text();
    const res = await fetch(`/api/shader/${encodeURIComponent(name)}`, {
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error(`Failed to load shader: ${name}`);
    }

    return await res.text();
  }

  toJSON() {
    const compute_list = [];
    for (const [key, value] of this.compiled_compute) {
      compute_list.push({ shader: key, compiled: !!value });
    }

    const render_list = [];
    for (const [key, value] of this.compiled_render) {
      render_list.push({ shader: key, compiled: !!value });
    }

    const header_list = [];
    for (const [key, value] of this.header_sources) {
      header_list.push({ header: key, loaded: !!value });
    }

    return { headers: header_list, compute: compute_list, render: render_list };
  }

  get_device(): GPUDevice | null {
    return this.device;
  }

  get(name: string): GPUShaderModule {
    if (!this.initialized || !this.device) {
      throw new Error("ShaderService not initialized. Call init() first.");
    }
    const mod = this.compiled_compute.get(name);
    if (!mod) {
      throw new Error(`Compute shader "${name}" not found.`);
    }
    return mod;
  }

  get_render(name: string): GPUShaderModule {
    if (!this.initialized || !this.device) {
      throw new Error("ShaderService not initialized. Call init() first.");
    }
    const mod = this.compiled_render.get(name);
    if (!mod) {
      throw new Error(`Render shader "${name}" not found.`);
    }
    return mod;
  }
}
